import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Shared destructive confirmation modal for admin bulk-delete flows.
 *
 * Props:
 *  - open (bool)
 *  - onClose (fn)
 *  - onConfirm (fn) — receives no args; parent handles the API call
 *  - busy (bool)
 *  - items ({id,label}[]) — selected rows
 *  - kind ("missions"|"orgs"|"users")
 *  - warning (string) — extra red warning above the confirm input
 */
export default function BulkDeleteModal({ open, onClose, onConfirm, busy, items, kind, warning }) {
  const { t, i18n } = useTranslation();
  const [text, setText] = useState("");
  const required = i18n.language === "fr" ? "SUPPRIMER" : "DELETE";
  const enabled = text.trim().toUpperCase() === required && !busy;

  const preview = useMemo(() => {
    const list = items.slice(0, 5).map((i) => i.label);
    const extra = items.length - 5;
    return { list, extra: extra > 0 ? extra : 0 };
  }, [items]);

  if (!open) return null;

  const titleKey = `admin_bulk.title_${kind}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="admin-bulk-delete-modal">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-lg">
        <h3 className="text-lg font-semibold text-slate-900" data-testid="bulk-delete-title">
          {t(titleKey, { count: items.length })}
        </h3>
        <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
          <ul className="list-disc pl-5 space-y-0.5">
            {preview.list.map((label) => (
              <li key={label} data-testid="bulk-delete-item">{label}</li>
            ))}
          </ul>
          {preview.extra > 0 && (
            <p className="mt-2 text-xs text-slate-500" data-testid="bulk-delete-extra">
              {t("admin_bulk.and_n_more", { count: preview.extra })}
            </p>
          )}
        </div>
        <p className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="bulk-delete-warning">
          {warning}
        </p>
        <label className="mt-4 block text-sm font-medium text-slate-700">
          {t("admin_bulk.type_to_confirm", { word: required })}
        </label>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-mono tracking-wider"
          data-testid="bulk-delete-confirm-input"
          placeholder={required}
          autoFocus
        />
        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            data-testid="bulk-delete-cancel"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={onConfirm}
            disabled={!enabled}
            className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="bulk-delete-confirm"
          >
            {busy ? t("common.loading") : t("admin_bulk.confirm_delete")}
          </button>
        </div>
      </div>
    </div>
  );
}
